import React, { useRef, useEffect, useCallback, useState, useImperativeHandle, forwardRef } from 'react';
import { getFilmstripUrl } from './api';
import { EffectMarker, EFFECT_DEFINITIONS } from './types';

interface TimelineProps {
  duration: number;
  currentTime: number;
  markers: number[];
  selectedMarkerIndex: number | null;
  onMarkerSelect: (index: number | null) => void;
  onSeek: (time: number) => void;
  onAddMarker: (time: number) => void;
  onRemoveMarker: (index: number) => void;
  onMoveMarker: (index: number, newTime: number) => void;
  effectMarkers: EffectMarker[];
  selectedEffectId: string | null;
  onSelectEffect: (id: string | null) => void;
  onMoveEffect: (id: string, newTime: number) => void;
  fps: number;
  videoId: string;
}

export interface TimelineHandle {
  zoomIn: () => void;
  zoomOut: () => void;
}

// Layer heights (top to bottom) - total 125px
const EFFECT_TRACK_HEIGHT = 30;
const RULER_HEIGHT = 20;
const FILMSTRIP_HEIGHT = 50;
const MARKER_AREA_HEIGHT = 30;
const TIMELINE_HEIGHT = EFFECT_TRACK_HEIGHT + RULER_HEIGHT + FILMSTRIP_HEIGHT + MARKER_AREA_HEIGHT;
const DRAG_THRESHOLD = 4;

function getEffectColor(effectId: string): string {
  return EFFECT_DEFINITIONS.find((d) => d.id === effectId)?.color ?? '#888';
}

export const Timeline = forwardRef<TimelineHandle, TimelineProps>(function Timeline({
  duration,
  currentTime,
  markers,
  selectedMarkerIndex,
  onMarkerSelect,
  onSeek,
  onAddMarker,
  onRemoveMarker,
  onMoveMarker,
  effectMarkers,
  selectedEffectId,
  onSelectEffect,
  onMoveEffect,
  fps,
  videoId,
}, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(800);
  const [zoom, setZoom] = useState(1);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingMarkerIndex, setDraggingMarkerIndex] = useState<number | null>(null);
  const [draggingEffectId, setDraggingEffectId] = useState<string | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [filmstripUrl, setFilmstripUrl] = useState<string | null>(null);
  const filmstripImg = useRef<HTMLImageElement | null>(null);

  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const pendingDragMarkerRef = useRef<number | null>(null);
  const pendingDragEffectRef = useRef<string | null>(null);
  const draggingMarkerTimeRef = useRef<number | null>(null);
  const draggingEffectCategoryRef = useRef<string | null>(null);

  const totalWidth = canvasWidth * zoom;
  const dpr = window.devicePixelRatio || 1;

  // Layer positions
  const effectTrackTop = 0;
  const rulerTop = EFFECT_TRACK_HEIGHT;
  const filmstripTop = rulerTop + RULER_HEIGHT;
  const markerAreaTop = filmstripTop + FILMSTRIP_HEIGHT;

  // Load filmstrip
  useEffect(() => {
    if (!videoId) return;
    getFilmstripUrl(videoId).then(url => {
      setFilmstripUrl(url);
      const img = new Image();
      img.src = url;
      img.onload = () => {
        filmstripImg.current = img;
      };
    });
  }, [videoId]);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCanvasWidth(entry.contentRect.width);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const timeToX = useCallback((time: number) => {
    if (duration <= 0) return 0;
    return (time / duration) * totalWidth - scrollOffset;
  }, [duration, totalWidth, scrollOffset]);

  const xToTime = useCallback((x: number) => {
    if (totalWidth <= 0) return 0;
    return Math.max(0, Math.min(((x + scrollOffset) / totalWidth) * duration, duration));
  }, [duration, totalWidth, scrollOffset]);

  // Find effect at position
  const findEffectAtPosition = useCallback((x: number, y: number): string | null => {
    if (y > EFFECT_TRACK_HEIGHT) return null;

    const effectTrackMid = EFFECT_TRACK_HEIGHT / 2;

    // Check timed effects (ranges)
    for (const em of effectMarkers.filter((e) => e.category === 'timed' && e.params.duration)) {
      const x1 = timeToX(em.time);
      const dur = Number(em.params.duration) || 0;
      const x2 = timeToX(em.time + dur);
      if (x >= x1 && x <= x2 && y >= effectTrackMid - 10 && y <= effectTrackMid + 10) {
        return em.id;
      }
    }

    // Check clip and transition effects (pills)
    for (const em of effectMarkers.filter((e) => e.category !== 'timed')) {
      const mx = timeToX(em.time);
      if (Math.abs(x - mx) < 20 && y >= effectTrackMid - 10 && y <= effectTrackMid + 10) {
        return em.id;
      }
    }

    return null;
  }, [effectMarkers, timeToX]);

  // Drawing
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || duration <= 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvasWidth * dpr;
    canvas.height = TIMELINE_HEIGHT * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvasWidth, TIMELINE_HEIGHT);

    // --- Effect track background ---
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, effectTrackTop, canvasWidth, EFFECT_TRACK_HEIGHT);

    ctx.strokeStyle = '#404060';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, rulerTop);
    ctx.lineTo(canvasWidth, rulerTop);
    ctx.stroke();

    const effectTrackMid = EFFECT_TRACK_HEIGHT / 2;

    // --- Draw timed effects as ranges ---
    effectMarkers
      .filter((em) => em.category === 'timed' && em.params.duration)
      .forEach((em) => {
        const x1 = timeToX(em.time);
        const dur = Number(em.params.duration) || 0;
        const x2 = timeToX(em.time + dur);
        const w = Math.max(x2 - x1, 4);
        if (x1 + w < -10 || x1 > canvasWidth + 10) return;

        const color = getEffectColor(em.effectId);
        const isSelected = em.id === selectedEffectId;

        if (isSelected) {
          ctx.shadowColor = color;
          ctx.shadowBlur = 8;
        }

        ctx.fillStyle = isSelected ? color + 'cc' : color + '55';
        ctx.beginPath();
        ctx.roundRect(x1, effectTrackMid - 8, w, 16, 3);
        ctx.fill();

        if (isSelected) {
          ctx.shadowBlur = 0;
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.roundRect(x1, effectTrackMid - 8, w, 16, 3);
          ctx.stroke();
        }

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        const labelX = x1 + w / 2;
        if (w > 20) {
          ctx.fillText(em.effectCode, labelX, effectTrackMid + 3);
        }
      });

    // --- Draw clip and transition effect markers as pills ---
    effectMarkers
      .filter((em) => em.category !== 'timed')
      .forEach((em) => {
        const x = timeToX(em.time);
        if (x < -30 || x > canvasWidth + 30) return;

        const color = getEffectColor(em.effectId);
        const isSelected = em.id === selectedEffectId;
        const label = em.effectCode;
        const pillW = Math.max(ctx.measureText(label).width + 10, 24);
        const pillH = 16;
        const px = x - pillW / 2;
        const py = effectTrackMid - pillH / 2;

        if (isSelected) {
          ctx.shadowColor = color;
          ctx.shadowBlur = 10;
        }

        ctx.fillStyle = isSelected ? color : color + 'cc';
        ctx.beginPath();
        ctx.roundRect(px, py, pillW, pillH, 3);
        ctx.fill();

        ctx.shadowBlur = 0;

        if (isSelected) {
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.roundRect(px, py, pillW, pillH, 3);
          ctx.stroke();
        }

        // Draw dashed line for transitions
        if (em.category === 'transition') {
          ctx.strokeStyle = color + '88';
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 2]);
          ctx.beginPath();
          ctx.moveTo(x, py + pillH);
          ctx.lineTo(x, EFFECT_TRACK_HEIGHT + RULER_HEIGHT);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(label, x, effectTrackMid + 3);
      });

    // --- Filmstrip ---
    if (filmstripImg.current) {
      const img = filmstripImg.current;
      const srcW = img.width;
      const srcH = img.height;
      const srcX = (scrollOffset / totalWidth) * srcW;
      const visibleRatio = canvasWidth / totalWidth;
      const srcVisW = srcW * visibleRatio;
      ctx.drawImage(img, srcX, 0, srcVisW, srcH, 0, filmstripTop, canvasWidth, FILMSTRIP_HEIGHT);
    } else {
      ctx.fillStyle = '#2a2a3e';
      ctx.fillRect(0, filmstripTop, canvasWidth, FILMSTRIP_HEIGHT);
    }

    // --- Ruler ---
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, rulerTop, canvasWidth, RULER_HEIGHT);

    const pixelsPerSec = totalWidth / duration;
    let interval = 1;
    if (pixelsPerSec < 20) interval = 5;
    if (pixelsPerSec < 10) interval = 10;
    if (pixelsPerSec > 100) interval = 0.5;
    if (pixelsPerSec > 200) interval = 0.25;

    ctx.fillStyle = '#808090';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';

    for (let t = 0; t <= duration; t += interval) {
      const x = timeToX(t);
      if (x < -20 || x > canvasWidth + 20) continue;

      ctx.strokeStyle = '#505060';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, rulerTop);
      ctx.lineTo(x, rulerTop + RULER_HEIGHT);
      ctx.stroke();

      const label = t >= 60
        ? `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`
        : `${t.toFixed(interval < 1 ? 1 : 0)}s`;
      ctx.fillText(label, x, rulerTop + 12);
    }

    // --- Segment shading on filmstrip ---
    const allPoints = [0, ...markers, duration];
    for (let i = 0; i < allPoints.length - 1; i++) {
      const x1 = timeToX(allPoints[i]);
      const x2 = timeToX(allPoints[i + 1]);
      ctx.fillStyle = i % 2 === 0 ? 'rgba(108,99,255,0.05)' : 'rgba(108,99,255,0.12)';
      ctx.fillRect(x1, filmstripTop, x2 - x1, FILMSTRIP_HEIGHT);
    }

    // --- Cut Markers ---
    markers.forEach((m, i) => {
      const x = timeToX(m);
      if (x < -10 || x > canvasWidth + 10) return;

      const isSelected = i === selectedMarkerIndex;

      // Dashed line through filmstrip
      ctx.strokeStyle = isSelected ? '#ffd93d' : '#ff6b6b';
      ctx.lineWidth = isSelected ? 2.5 : 1;
      ctx.setLineDash(isSelected ? [] : [3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, filmstripTop);
      ctx.lineTo(x, markerAreaTop);
      ctx.stroke();
      ctx.setLineDash([]);

      // Glow for selected marker
      if (isSelected) {
        ctx.shadowColor = '#ffd93d';
        ctx.shadowBlur = 12;
      }

      // Flag shape
      ctx.fillStyle = isSelected ? '#ffd93d' : '#ff6b6b';
      ctx.beginPath();
      ctx.moveTo(x, markerAreaTop);
      ctx.lineTo(x - 7, markerAreaTop + 8);
      ctx.lineTo(x - 7, markerAreaTop + MARKER_AREA_HEIGHT);
      ctx.lineTo(x + 7, markerAreaTop + MARKER_AREA_HEIGHT);
      ctx.lineTo(x + 7, markerAreaTop + 8);
      ctx.closePath();
      ctx.fill();

      ctx.shadowBlur = 0;

      // White outline on selected
      if (isSelected) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x, markerAreaTop);
        ctx.lineTo(x - 7, markerAreaTop + 8);
        ctx.lineTo(x - 7, markerAreaTop + MARKER_AREA_HEIGHT);
        ctx.lineTo(x + 7, markerAreaTop + MARKER_AREA_HEIGHT);
        ctx.lineTo(x + 7, markerAreaTop + 8);
        ctx.closePath();
        ctx.stroke();
      }

      ctx.fillStyle = isSelected ? '#000' : '#000';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${i + 1}`, x, markerAreaTop + 22);
    });

    // --- Playhead ---
    const px = timeToX(currentTime);
    ctx.strokeStyle = '#4ecdc4';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px, 0);
    ctx.lineTo(px, TIMELINE_HEIGHT);
    ctx.stroke();

    ctx.fillStyle = '#4ecdc4';
    ctx.beginPath();
    ctx.moveTo(px - 6, 0);
    ctx.lineTo(px + 6, 0);
    ctx.lineTo(px, 8);
    ctx.closePath();
    ctx.fill();
  }, [canvasWidth, currentTime, duration, markers, selectedMarkerIndex, scrollOffset, totalWidth, effectMarkers, selectedEffectId, timeToX, dpr]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Zoom around playhead
  const zoomAroundPlayhead = useCallback((newZoom: number) => {
    if (duration <= 0) return;
    const clamped = Math.max(0.5, Math.min(5, newZoom));
    const newTotal = canvasWidth * clamped;
    const playheadRatio = currentTime / duration;
    const playheadInTotal = playheadRatio * newTotal;
    const newOffset = Math.max(0, Math.min(playheadInTotal - canvasWidth / 2, newTotal - canvasWidth));
    setZoom(clamped);
    setScrollOffset(newOffset);
  }, [duration, canvasWidth, currentTime]);

  // Zoom handlers
  useImperativeHandle(ref, () => ({
    zoomIn: () => zoomAroundPlayhead(zoom * 1.2),
    zoomOut: () => zoomAroundPlayhead(zoom / 1.2),
  }), [zoom, zoomAroundPlayhead]);

  const clearDragState = useCallback(() => {
    dragStartRef.current = null;
    pendingDragMarkerRef.current = null;
    pendingDragEffectRef.current = null;
    draggingMarkerTimeRef.current = null;
    draggingEffectCategoryRef.current = null;
    setIsDragging(false);
    setDraggingMarkerIndex(null);
    setDraggingEffectId(null);
    setIsScrubbing(false);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check effect track first
    if (y <= EFFECT_TRACK_HEIGHT) {
      const effectId = findEffectAtPosition(x, y);
      if (effectId) {
        onSelectEffect(effectId);
        onMarkerSelect(null);
        pendingDragEffectRef.current = effectId;
        pendingDragMarkerRef.current = null;
        // Save effect category for snapping logic during drag
        const em = effectMarkers.find((m) => m.id === effectId);
        draggingEffectCategoryRef.current = em?.category || null;
        dragStartRef.current = { x, y };
        return;
      }
    }

    // Check cut markers across ruler + filmstrip + flag area
    const rulerTop = EFFECT_TRACK_HEIGHT;
    if (y >= rulerTop) {
      for (let i = 0; i < markers.length; i++) {
        const mx = timeToX(markers[i]);
        if (Math.abs(x - mx) < 14) {
          onMarkerSelect(i);
          onSelectEffect(null);
          pendingDragMarkerRef.current = i;
          pendingDragEffectRef.current = null;
          draggingMarkerTimeRef.current = markers[i];
          dragStartRef.current = { x, y };
          return;
        }
      }
    }

    // Scrubbing - deselect only here
    onMarkerSelect(null);
    onSelectEffect(null);
    pendingDragMarkerRef.current = null;
    pendingDragEffectRef.current = null;
    dragStartRef.current = null;
    setIsScrubbing(true);
    onSeek(xToTime(x));
  }, [markers, timeToX, xToTime, onSeek, onMarkerSelect, onSelectEffect, findEffectAtPosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const time = xToTime(x);

    // Check if pending drag should be promoted to actual drag
    if (dragStartRef.current && (pendingDragMarkerRef.current !== null || pendingDragEffectRef.current !== null)) {
      const dx = Math.abs(x - dragStartRef.current.x);
      const dy = Math.abs((e.clientY - rect.top) - dragStartRef.current.y);
      if (dx + dy < DRAG_THRESHOLD) return;
      if (pendingDragMarkerRef.current !== null) {
        setIsDragging(true);
        setDraggingMarkerIndex(pendingDragMarkerRef.current);
      }
      if (pendingDragEffectRef.current !== null) {
        setDraggingEffectId(pendingDragEffectRef.current);
      }
      pendingDragMarkerRef.current = null;
      pendingDragEffectRef.current = null;
      dragStartRef.current = null;
    }

    if (draggingEffectId !== null) {
      // Use saved category from ref to avoid closure staleness
      const isTransition = draggingEffectCategoryRef.current === 'transition';
      if (isTransition && markers.length > 0) {
        // Snap to nearest marker for transitions
        let nearest = markers[0];
        let minDist = Math.abs(time - nearest);
        for (const m of markers) {
          const dist = Math.abs(time - m);
          if (dist < minDist) {
            minDist = dist;
            nearest = m;
          }
        }
        onMoveEffect(draggingEffectId, nearest);
      } else {
        onMoveEffect(draggingEffectId, time);
      }
    } else if (isDragging && draggingMarkerIndex !== null && draggingMarkerTimeRef.current !== null) {
      const trackedTime = draggingMarkerTimeRef.current;
      const currentIndex = markers.findIndex((m) => Math.abs(m - trackedTime) < 0.005);
      if (currentIndex >= 0) {
        const rounded = Math.round(time * 100) / 100;
        onMoveMarker(currentIndex, time);
        draggingMarkerTimeRef.current = rounded;
      }
    } else if (isScrubbing) {
      onSeek(time);
    }
  }, [draggingEffectId, isDragging, draggingMarkerIndex, isScrubbing, xToTime, onMoveEffect, onMoveMarker, onSeek, effectMarkers, markers]);

  const handleMouseUp = useCallback(() => {
    clearDragState();
  }, [clearDragState]);

  // Wheel handler with zoom around playhead
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      zoomAroundPlayhead(zoom - e.deltaY * 0.005);
    } else {
      const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      const maxScroll = Math.max(0, canvasWidth * zoom - canvasWidth);
      setScrollOffset((prev) => Math.max(0, Math.min(prev + delta, maxScroll)));
    }
  }, [zoom, canvasWidth, zoomAroundPlayhead]);

  useEffect(() => {
    const maxScroll = Math.max(0, totalWidth - canvasWidth);
    if (scrollOffset > maxScroll) setScrollOffset(maxScroll);
  }, [zoom, canvasWidth, totalWidth, scrollOffset]);

  useEffect(() => {
    const onGlobalMouseUp = () => {
      clearDragState();
    };
    window.addEventListener('mouseup', onGlobalMouseUp);
    return () => window.removeEventListener('mouseup', onGlobalMouseUp);
  }, [clearDragState]);

  const cursor = draggingMarkerIndex !== null || draggingEffectId !== null
    ? 'grabbing'
    : isScrubbing
      ? 'col-resize'
      : 'pointer';

  const handleAddMarkerClick = () => {
    onAddMarker(currentTime);
  };

  return (
    <div className="at-timeline-container">
      <div className="at-timeline-header">
        <span>Таймлайн</span>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span className="at-marker-count">Маркеров: {markers.length} | Эффектов: {effectMarkers.length}</span>
          <button className="at-zoom-btn" onClick={() => zoomAroundPlayhead(zoom / 1.2)}>-</button>
          <span style={{ fontSize: '11px', color: '#7F8489' }}>{Math.round(zoom * 100)}%</span>
          <button className="at-zoom-btn" onClick={() => zoomAroundPlayhead(zoom * 1.2)}>+</button>
          <button
            className="at-add-marker-btn"
            onClick={handleAddMarkerClick}
            title="Добавить маркер в текущей позиции (M)"
          >
            + Маркер
          </button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="at-timeline-wrapper"
      >
        <canvas
          ref={canvasRef}
          className="at-timeline-canvas"
          style={{ width: '100%', height: TIMELINE_HEIGHT, cursor }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        />
      </div>
      <div className="at-timeline-info">
        <span>Клик: перемещение | Drag маркера: перемещение | Колесо+Ctrl: зум | Shift+колесо: прокрутка</span>
        <span>M: маркер | +/-: зум | Del: удалить</span>
      </div>
    </div>
  );
});
